'use client';

import { c as _c } from "react/compiler-runtime";
import { Heading, HorizontalOverflowCarousel, LayoutContainer } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useMemo } from 'react';
import { getVariationLabel } from '@libs/algolia';
import { createItemsViewEvent, useViewItemListImpressions } from '@libs/analytics';
import { useWhiteSpaceCalculation } from '@libs/contentful';
import { getCarouselConfig, isQuickAddEligible, mapYmalProductsToAnalyticsProducts, transformYmalProductForDisplay } from '../lib';
import { ProductCardWithVariations } from './ProductCardWithVariations';
import { YmalProductsSectionTracker } from './YmalProductsSectionTracker';
import { YmalProductTracker } from './YmalProductTracker';
import type { YmalProductsCarouselProps } from './ymalProductsCarousel.types';
import './YmalProductsCarousel.scss';

/** Presentational product carousel. Callers own feature flags, data fetching, title, and min count. */
export const YmalProductsCarousel = t0 => {
  const $ = _c(41);
  const {
    products,
    title,
    minProducts,
    frefAttribution,
    showQuickAdd: t1,
    viewItemList
  } = t0;
  const showQuickAdd = t1 === undefined ? false : t1;
  const whiteSpaceCalculate = useWhiteSpaceCalculation();
  const carouselLength = products.length;
  let t2;
  bb0: {
    if (!viewItemList) {
      let t3;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[0] = t3;
      } else {
        t3 = $[0];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[1] !== products || $[2] !== viewItemList.itemListId || $[3] !== viewItemList.itemListName) {
      t3 = createItemsViewEvent(mapYmalProductsToAnalyticsProducts(products), viewItemList.itemListName, viewItemList.itemListId);
      $[1] = products;
      $[2] = viewItemList.itemListId;
      $[3] = viewItemList.itemListName;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = t3.ecommerce.items;
  }
  const impressionItems = t2;
  let t3;
  if ($[5] !== impressionItems) {
    t3 = {
      items: impressionItems
    };
    $[5] = impressionItems;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    onVisible
  } = useViewItemListImpressions(t3);
  if (carouselLength < minProducts) {
    return null;
  }
  let carouselConfig;
  let t4;
  if ($[7] !== carouselLength) {
    const {
      disableArrows,
      ...t5
    } = getCarouselConfig(carouselLength);
    carouselConfig = t5;
    t4 = cn("product-carousel__carousel", disableArrows && "product-carousel__carousel--disable-arrows");
    $[7] = carouselLength;
    $[8] = carouselConfig;
    $[9] = t4;
  } else {
    carouselConfig = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[10] !== carouselConfig || $[11] !== t4) {
    t5 = {
      className: t4,
      ...carouselConfig
    };
    $[10] = carouselConfig;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const carouselOptions = t5;
  let t6;
  if ($[13] !== carouselLength || $[14] !== carouselOptions.breakpoints || $[15] !== whiteSpaceCalculate) {
    t6 = translate => {
      const {
        gap,
        perView
      } = carouselOptions.breakpoints["560"];
      if (whiteSpaceCalculate) {
        return whiteSpaceCalculate(translate, perView, gap, carouselLength);
      }
      return translate;
    };
    $[13] = carouselLength;
    $[14] = carouselOptions.breakpoints;
    $[15] = whiteSpaceCalculate;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const modifier = t6;
  let t7;
  if ($[17] !== frefAttribution || $[18] !== onVisible || $[19] !== products || $[20] !== showQuickAdd || $[21] !== viewItemList) {
    let t8;
    if ($[23] !== frefAttribution || $[24] !== onVisible || $[25] !== showQuickAdd || $[26] !== viewItemList) {
      t8 = (product, index) => {
        const productData = transformYmalProductForDisplay(product, index, frefAttribution);
        const variations = getVariationLabel(product.variations, product.variationData);
        return <YmalProductTracker key={product.sku} product={product} position={index + 1} frefAttribution={frefAttribution} onView={viewItemList ? () => onVisible(index) : undefined}><ProductCardWithVariations {...productData} variations={variations} showQuickAdd={showQuickAdd && isQuickAddEligible(product)} /></YmalProductTracker>;
      };
      $[23] = frefAttribution;
      $[24] = onVisible;
      $[25] = showQuickAdd;
      $[26] = viewItemList;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    t7 = products.map(t8);
    $[17] = frefAttribution;
    $[18] = onVisible;
    $[19] = products;
    $[20] = showQuickAdd;
    $[21] = viewItemList;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[28] !== carouselOptions || $[29] !== modifier || $[30] !== t7) {
    t8 = <HorizontalOverflowCarousel {...carouselOptions} modifier={modifier}>{t7}</HorizontalOverflowCarousel>;
    $[28] = carouselOptions;
    $[29] = modifier;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const carousel = t8;
  let t9;
  if ($[32] !== title) {
    t9 = <Heading tagName="h2" size="title">{title}</Heading>;
    $[32] = title;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== carousel || $[35] !== products || $[36] !== viewItemList) {
    t10 = viewItemList ? carousel : <YmalProductsSectionTracker products={products}>{carousel}</YmalProductsSectionTracker>;
    $[34] = carousel;
    $[35] = products;
    $[36] = viewItemList;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <LayoutContainer className="you-may-also-like-products-carousel" data-testid="you-may-also-like-products-carousel">{t9}{t10}</LayoutContainer>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11;
};